import React, { lazy, useEffect } from "react";
import { ToastProvider } from "react-toast-notifications";
import { useDispatch, useSelector } from "react-redux";
import { useRoutes } from "react-router-dom";
import { useNavigate } from "react-router";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { BlockScreen, Loadable, Loader, ModalWrapper, Notification, OverlayWrapper, Toast } from "@shared/components";
import { NamesOfParentRoutes } from "@shared/constants";
import { AuthGuard, GuestGuard } from "@shared/guards";
import { actions, selectors } from "@shared/store";
import { actions as authActions, selectors as authSelectors } from "@containers/Auth/store";

import config from "../../config";
import { usePageViewTracking } from "./hooks";

const AuthContainer = Loadable(lazy(() => import("../Auth/containers/AuthContainer/AuthContainer")));
const ApplicatonContainer = Loadable(lazy(() => import("../App/ApplicatonContainer")));

const routes = [
  {
    path: `${NamesOfParentRoutes.AUTH}/*`,
    element: (
      <GuestGuard>
        <AuthContainer />
        <BlockScreen />
      </GuestGuard>
    ),
  },
  {
    path: `/*`,
    element: (
      <AuthGuard>
        <ApplicatonContainer />
        <BlockScreen />
      </AuthGuard>
    ),
  },
];

const App = () => {
  const content = useRoutes(routes);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const notification = useSelector(selectors.getNotification());
  const navigateURL = useSelector(selectors.getNavigateURL());
  const bootstrapped = useSelector(authSelectors.bootstrapped());
  const authentificated = useSelector(authSelectors.authentificated());

  usePageViewTracking(authentificated);

  // The access token lives in memory, so on load we silently rebuild the session from the
  // http-only refresh cookie before rendering any route. Gating here (above both guards) stops
  // AuthGuard from redirecting an authenticated user to /auth before the refresh resolves.
  useEffect(() => {
    dispatch(authActions.bootstrap.request());
  }, [dispatch]);

  useEffect(() => {
    if (navigateURL) {
      navigate(navigateURL);
      dispatch(actions.navigate(""));
    }
  }, [navigateURL, dispatch, navigate]);

  return (
    <GoogleOAuthProvider clientId={config.googleClientId}>
      <ToastProvider components={{ Toast: Toast }} placement="bottom-left">
        <Notification notification={notification} />
        <ModalWrapper />
        <OverlayWrapper />
        <div className="App">{bootstrapped ? content : <Loader position="center" />}</div>
      </ToastProvider>
    </GoogleOAuthProvider>
  );
};

export default App;
